import { syncMemberChange } from '../members/memberSync';
import PlainImage from '../images/PlainImage';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FiUserX } from 'react-icons/fi';
import api from '../../api/api';
import PageLayout from '../../components/layout/PageLayout';
import {
  AccountBoundary,
  useSettings,
  useRequestScope,
  SettingsHeader,
  Confirm,
  unwrap,
} from './shared';
export default function BlockedUsersPage() {
  return <AccountBoundary component={Blocked} />;
}
function Blocked({ user }) {
  const { t, style } = useSettings(),
    client = useQueryClient(),
    request = useRequestScope(user);
  const [items, setItems] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [attempt, setAttempt] = useState(0),
    [selected, setSelected] = useState(null),
    [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const listRequest = useRef(null);
  const listRoot = useRef(null);
  const focusAfterRemoval = useRef(null);
  useEffect(() => {
    if (busy || selected || !focusAfterRemoval.current) return;
    const candidates = focusAfterRemoval.current;
    focusAfterRemoval.current = null;
    const buttons = Array.from(listRoot.current?.querySelectorAll('[data-unblock-id]') || []);
    const target =
      candidates
        .map((id) => buttons.find((button) => button.dataset.unblockId === String(id)))
        .find(Boolean) || listRoot.current?.querySelector('.pp-blocked-empty');
    target?.focus({ preventScroll: true });
  }, [busy, selected, items]);
  useEffect(() => {
    if (!user) return;
    const r = request();
    listRequest.current = r;
    setLoading(true);
    setError('');
    api
      .getAuth('/safety/blocks', r.options)
      .then((x) => {
        if (r.current()) {
          const list = unwrap(x);
          if (!Array.isArray(list)) throw Error();
          setItems(list);
        }
      })
      .catch(() => {
        if (r.current()) setError(t('safety.blockedUsersLoadError'));
      })
      .finally(() => {
        if (r.mounted() && listRequest.current === r) {
          setLoading(false);
          if (!r.current()) setError(t('common.retryLater'));
        }
        if (listRequest.current === r) listRequest.current = null;
        r.finish();
      });
    return () => {
      if (listRequest.current === r) listRequest.current = null;
      r.cancel();
    };
    // AccountBoundary resets data on session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  const unblock = async () => {
    if (lock.current || !selected) return;
    const id = selected.id;
    lock.current = true;
    // Like build60 onMutate: an earlier list read must not undo a successful unblock.
    listRequest.current?.cancel();
    listRequest.current = null;
    setLoading(false);
    setBusy(true);
    setError('');
    const r = request();
    try {
      await api.deleteAuth(`/safety/blocks/${id}`, r.options);
      if (r.current()) {
        const synced = await syncMemberChange(client, {
          viewer: user,
          target: selected,
          kind: 'unblock',
          current: r.current,
        });
        if (synced && r.current()) {
          const index = items.findIndex((item) => item.id === id);
          focusAfterRemoval.current = [
            ...items.slice(index + 1),
            ...items.slice(0, index).reverse(),
          ].map((item) => item.id);
          setItems((xs) => xs.filter((x) => x.id !== id));
        }
      }
    } catch {
      if (r.current()) setError(t('safety.failedTitle'));
    } finally {
      lock.current = false;
      if (r.mounted()) {
        setBusy(false);
        setSelected(null);
        if (!r.current()) setError(t('common.retryLater'));
      }
      r.finish();
    }
  };
  if (!user) return <Navigate replace to="/login?redirectUrl=%2Fmypage%2Fblocked" />;
  return (
    <PageLayout paddingTop={0}>
      <div className="pp-settings pp-blocked" style={style} ref={listRoot}>
        <SettingsHeader title={t('safety.blockedUsers')} busy={busy} />
        <p className="pp-settings-intro">{t('safety.blockedUsersDescription')}</p>
        {loading && <p role="status">{t('common.loading')}</p>}
        {!loading && items?.length === 0 && (
          <div className="pp-blocked-empty" tabIndex={-1}>
            <FiUserX size={34} />
            <p>{t('safety.blockedUsersEmpty')}</p>
          </div>
        )}
        {items?.map((u) => (
          <div className="pp-blocked-row" key={u.id}>
            <Avatar user={u} />
            <span>{u.nickname}</span>
            <button
              type="button"
              data-unblock-id={u.id}
              disabled={busy}
              onClick={() => setSelected(u)}
            >
              {t('safety.unblockAction')}
            </button>
          </div>
        ))}
        {error && (
          <div role="alert">
            <p>{error}</p>
            <button
              type="button"
              disabled={busy || loading}
              onClick={() => setAttempt((n) => n + 1)}
            >
              {t('common.retry')}
            </button>
          </div>
        )}
        {selected && (
          <Confirm
            title={t('safety.unblockTitle')}
            body={t('safety.unblockQuestion', { name: selected.nickname })}
            cancel={t('common.cancel')}
            confirm={t('safety.unblockAction')}
            busy={busy}
            onCancel={() => setSelected(null)}
            onConfirm={unblock}
          />
        )}
      </div>
    </PageLayout>
  );
}
function Avatar({ user }) {
  return (
    <div className="pp-blocked-avatar">
      {user.profileImageUrl ? (
        <PlainImage src={user.profileImageUrl} alt="" />
      ) : (
        user.nickname?.slice(0, 1)
      )}
    </div>
  );
}
